import React, { useState } from "react";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { imgUrl } from "../lib/api";

const CartDrawer = () => {
  const { open, setOpen, items, remove, updateQty, total } = useCart();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const res = await api.post("/checkout/session", {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
        })),
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Checkout failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-50 bg-[#1A1F1B]/35 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="cart-backdrop"
        />
      )}
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[440px] bg-[#F7F6F2] border-l border-[#D8D3CC] transform transition-transform duration-500 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="cart-drawer"
      >
        <div className="flex flex-col h-full">
          <div className="px-6 py-5 flex items-center justify-between border-b border-[#D8D3CC]">
            <div>
              <div className="label-eyebrow">Your Cart</div>
              <div className="font-serif-display text-2xl text-[#1A1F1B]">
                Studio Selections
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-2 hover:bg-[#EFECE5]"
              aria-label="Close cart"
              data-testid="cart-close-button"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            {items.length === 0 ? (
              <div className="text-center mt-20">
                <ShoppingBag size={28} strokeWidth={1} className="mx-auto text-[#849383]" />
                <p className="mt-4 text-sm text-[#4E5C50]">Your cart is empty.</p>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    navigate("/shop");
                  }}
                  className="btn-secondary mt-6"
                  data-testid="cart-browse-shop"
                >
                  Browse Shop
                </button>
              </div>
            ) : (
              <ul className="space-y-6">
                {items.map((i) => (
                  <li
                    key={i.product_id}
                    className="flex gap-4 pb-6 border-b border-[#D8D3CC] last:border-b-0"
                    data-testid={`cart-item-${i.product_id}`}
                  >
                    <div className="w-20 h-24 bg-[#EFECE5] flex-shrink-0">
                      <img
                        src={imgUrl(i.image_url)}
                        alt={i.title}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="font-serif-display text-lg text-[#1A1F1B] leading-tight">
                        {i.title}
                      </div>
                      <div className="label-eyebrow mt-1">{i.kind}</div>
                      <div className="mt-3 flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => updateQty(i.product_id, i.quantity - 1)}
                          className="w-7 h-7 border border-[#D8D3CC] flex items-center justify-center hover:bg-[#EFECE5]"
                          aria-label="Decrease"
                          data-testid={`cart-qty-decrease-${i.product_id}`}
                        >
                          <Minus size={12} />
                        </button>
                        <span className="text-sm w-6 text-center" data-testid={`cart-qty-${i.product_id}`}>
                          {i.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQty(i.product_id, i.quantity + 1)}
                          className="w-7 h-7 border border-[#D8D3CC] flex items-center justify-center hover:bg-[#EFECE5]"
                          aria-label="Increase"
                          data-testid={`cart-qty-increase-${i.product_id}`}
                        >
                          <Plus size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(i.product_id)}
                          className="ml-auto text-xs text-[#849383] hover:text-[#3B4A3F] underline underline-offset-4"
                          data-testid={`cart-remove-${i.product_id}`}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                    <div className="font-serif-display text-lg text-[#1A1F1B]">
                      ${(i.price * i.quantity).toFixed(2)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {items.length > 0 && (
            <div className="border-t border-[#D8D3CC] px-6 py-5 space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="label-eyebrow">Subtotal</span>
                <span className="font-serif-display text-2xl text-[#1A1F1B]" data-testid="cart-subtotal">
                  ${total.toFixed(2)}
                </span>
              </div>
              <button
                type="button"
                onClick={checkout}
                disabled={loading}
                className="btn-primary w-full disabled:opacity-50"
                data-testid="cart-checkout-button"
              >
                {loading ? "Preparing…" : "Proceed to Checkout"}
              </button>
              <p className="text-xs text-[#849383] text-center">
                Secure checkout via Stripe. Shipping calculated after order.
              </p>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default CartDrawer;
